import { useEffect, useMemo, useState } from "react";
import { Alert, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Table } from "reactstrap";

import useConfig from "../../../actions/Tenants/config/configHook";
import { useDevicesQuery } from "../../../hooks/useDeviceMutations";
import { RoomLink, useTenantFacilityLocations, useUpdateRoomLinks } from "../../../hooks/useFacilityLocationQueries";
import { RipeningRoomStatus, useRipeningRooms } from "../../../hooks/useRipeningPrograms";
import { toast } from "../../../utils/toast";
import { Button } from "../../Helper/Button";
import { Loading } from "../../Helper/Loading";

const RIPENING_ROOM_GATEWAY = "ripening_room_gateway";

interface Cell {
    location_id: string;
    label: string;
    external_id?: string | null;
    gateway?: string | null;
    controller_room?: string | null;
}

const RUN_STATES: Record<string, string> = {
    ripening: "ripening",
    storage: "storage",
    paused: "paused",
    off: "idle",
    stopped: "idle",
    emergency_stop: "emergency stop",
};

/** What a room is doing now, in words, so a person can tell which room it is. */
export function describeRoom(room: RipeningRoomStatus): string {
    if (room.status === "stale") return "no connection";
    if (room.status !== "online") return "no status yet";
    const run = room.run;
    const state = String(run?.state ?? "").toLowerCase();
    return [RUN_STATES[state] ?? (state || "idle"), run?.recipe, run?.product].filter(Boolean).join(" · ");
}

const shortId = (uid: string) => (uid.length > 12 ? `${uid.slice(0, 8)}…` : uid);

/** The gateway most cells already use, else the first one. */
function defaultGateway(cells: Cell[], gateways: string[]): string {
    const counts = new Map<string, number>();
    cells.forEach((cell) => {
        if (cell.gateway && gateways.includes(cell.gateway)) counts.set(cell.gateway, (counts.get(cell.gateway) ?? 0) + 1);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? gateways[0] ?? "";
}

/** Link the client's ripening rooms (cells) to the rooms of a gateway, all in one go. */
export default function RoomLinksModal({ tenantId, isOpen, toggle }: { tenantId: string; isOpen: boolean; toggle: () => void }) {
    const { data: devices, isLoading: devicesLoading } = useDevicesQuery();
    const { data: cellsData, isLoading: cellsLoading } = useTenantFacilityLocations(isOpen ? tenantId : undefined, { offset: 0, limit: 1000, type: "ripening_room" });
    const cells: Cell[] = useMemo(() => (cellsData?.results ?? []).map((location) => ({
        location_id: location.location_id,
        label: location.label,
        external_id: location.external_id,
        gateway: location.gateway,
        controller_room: location.controller_room,
    })), [cellsData]);
    const gateways = useMemo(
        () => (devices ?? []).filter((device) => device.type === RIPENING_ROOM_GATEWAY && device.tenant_id === tenantId),
        [devices, tenantId],
    );
    const [gateway, setGateway] = useState("");
    // * Only the cells the user changed: location_id -> room number ("" = not linked).
    const [draft, setDraft] = useState<Record<string, string>>({});
    const saveMutation = useUpdateRoomLinks();

    useEffect(() => {
        if (!isOpen) return;
        setDraft({});
        setGateway((current) => current || defaultGateway(cells, gateways.map((g) => g.UID)));
    }, [isOpen, cells, gateways]);

    // * Rendered inside the tenant's config provider. Without RipeWise the rooms are not loaded: the numbers are typed.
    const ripewiseEnabled = !!useConfig().ripewise_config?.enabled;
    const { data: rooms, isError: roomsError, isLoading: roomsLoading } = useRipeningRooms(isOpen && ripewiseEnabled ? gateway : null, { tenantId });
    const roomIds = useMemo(() => (rooms ?? []).map((room) => room.room_id).sort(), [rooms]);

    // * The room a cell has on the chosen gateway now ("" = none; a cell on another gateway keeps that link until changed).
    const savedRoomOf = (cell: Cell) => (cell.gateway === gateway ? cell.controller_room ?? "" : "");
    const roomOf = (cell: Cell) => (cell.location_id in draft ? draft[cell.location_id] : savedRoomOf(cell));
    // * Which cells hold each room of this gateway, with the draft applied.
    const holders = useMemo(() => {
        const result = new Map<string, string[]>();
        cells.forEach((cell) => {
            const room = roomOf(cell);
            if (room) result.set(room, [...(result.get(room) ?? []), cell.label]);
        });
        return result;
    }, [cells, draft, gateway]);
    const duplicates = Array.from(holders.entries()).filter(([, labels]) => labels.length > 1);
    const changed = Object.keys(draft).length;

    const setRoom = (cell: Cell, room: string) => {
        setDraft((prev) => {
            const next = { ...prev };
            if (room === savedRoomOf(cell)) delete next[cell.location_id];
            else next[cell.location_id] = room;
            return next;
        });
    };
    const changeGateway = (value: string) => {
        setGateway(value);
        setDraft({});
    };

    const save = () => {
        const links: RoomLink[] = Object.entries(draft).map(([location_id, room]) => ({
            location_id,
            gateway: room ? gateway : null,
            controller_room: room || null,
        }));
        saveMutation.mutate({ tenantId, links }, {
            onSuccess: () => {
                toast.success(`${links.length} room ${links.length === 1 ? "link" : "links"} saved`);
                toggle();
            },
        });
    };

    const roomLabel = (roomId: string) => {
        const room = rooms?.find((item) => item.room_id === roomId);
        return room ? `${roomId} · ${describeRoom(room)}` : roomId;
    };

    const renderRoomInput = (cell: Cell) => {
        const value = roomOf(cell);
        if (roomsError || !ripewiseEnabled) {
            return (
                <Input
                    bsSize="sm"
                    aria-label={`Room of ${cell.label}`}
                    value={value}
                    placeholder="Room number"
                    onChange={(event) => setRoom(cell, event.target.value.trim())}
                />
            );
        }
        const options = value && !roomIds.includes(value) ? [value, ...roomIds] : roomIds;
        return (
            <Input type="select" bsSize="sm" aria-label={`Room of ${cell.label}`} value={value} onChange={(event) => setRoom(cell, event.target.value)}>
                <option value="">Not linked</option>
                {options.map((roomId) => {
                    const others = (holders.get(roomId) ?? []).filter((label) => label !== cell.label);
                    return (
                        <option key={roomId} value={roomId}>
                            {roomLabel(roomId)}{others.length > 0 ? ` (now ${others.join(", ")})` : ""}
                        </option>
                    );
                })}
            </Input>
        );
    };

    const loading = devicesLoading || cellsLoading;
    return (
        <Modal size="xl" isOpen={isOpen} toggle={toggle} scrollable>
            <ModalHeader toggle={toggle}>Room links</ModalHeader>
            <ModalBody>
                {loading && <Loading />}
                {!loading && gateways.length === 0 && (
                    <Alert color="info" className="mb-0">This client has no ripening room gateway yet. Add one under Device management first.</Alert>
                )}
                {!loading && gateways.length > 0 && <>
                    <p className="text-muted mb-3">
                        Pick the gateway, then the room each cell is. Each room shows what it is doing now, so you can recognise it.
                    </p>
                    <FormGroup className="mb-3" style={{ maxWidth: "32rem" }}>
                        <Label for="room-links-gateway">Gateway</Label>
                        <Input id="room-links-gateway" type="select" value={gateway} onChange={(event) => changeGateway(event.target.value)}>
                            {gateways.map((device) => (
                                <option key={device.UID} value={device.UID}>{device.name && device.name !== device.UID ? `${device.name} (${shortId(device.UID)})` : device.UID}</option>
                            ))}
                        </Input>
                    </FormGroup>
                    {roomsLoading && <div className="small text-muted mb-2">Loading the rooms of this gateway…</div>}
                    {roomsError && (
                        <Alert color="warning" className="py-2">The rooms of this gateway cannot be loaded right now. You can still type the room numbers.</Alert>
                    )}
                    {duplicates.length > 0 && (
                        <Alert color="danger" className="py-2" data-testid="room-links-duplicates">
                            {duplicates.map(([room, labels]) => <div key={room}>Room {room} is linked to more than one cell: {labels.join(", ")}</div>)}
                        </Alert>
                    )}
                    <Table size="sm" className="align-middle">
                        <thead>
                            <tr>
                                <th>Cell</th>
                                <th>Room</th>
                                <th>Linked now</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cells.map((cell) => (
                                <tr key={cell.location_id} data-testid={`room-link-${cell.label}`} className={cell.location_id in draft ? "table-info" : undefined}>
                                    <td className="text-nowrap">
                                        <div className="fw-semibold">{cell.label}</div>
                                        {cell.external_id && cell.external_id !== cell.label && <div className="small text-muted">{cell.external_id}</div>}
                                    </td>
                                    <td style={{ minWidth: "22rem" }}>{renderRoomInput(cell)}</td>
                                    <td className="small text-muted text-nowrap">
                                        {!cell.gateway && "Not linked"}
                                        {cell.gateway === gateway && `Room ${cell.controller_room}`}
                                        {cell.gateway && cell.gateway !== gateway && `Other gateway ${shortId(cell.gateway)}, room ${cell.controller_room}`}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>}
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={toggle} disabled={saveMutation.isPending}>Cancel</Button>
                <Button color="primary" onClick={save} isLoading={saveMutation.isPending} disabled={changed === 0 || duplicates.length > 0 || saveMutation.isPending}>
                    {changed > 0 ? `Save ${changed} ${changed === 1 ? "change" : "changes"}` : "Save"}
                </Button>
            </ModalFooter>
        </Modal>
    );
}
